import { useEffect, useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import {
  LayoutDashboard,
  FileText,
  UtensilsCrossed,
  Calendar,
  Bell,
  MessageSquare,
  Megaphone,
  LogOut,
  BarChart3,
  Image as ImageIcon,
  UserCog,
  Images,
  MonitorPlay,
  Clock3,
} from "lucide-react";

const ITEMS = [
  { to: "/admin", label: "Resumen", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard" },
  { to: "/admin/contenido", label: "Contenido", icon: FileText, testid: "admin-nav-contenido" },
  { to: "/admin/media", label: "Imágenes", icon: ImageIcon, testid: "admin-nav-media" },
  { to: "/admin/galeria-visitantes", label: "Galería visitantes", icon: Images, testid: "admin-nav-galeria-visitantes" },
  { to: "/admin/carta", label: "Carta", icon: UtensilsCrossed, testid: "admin-nav-carta" },
  { to: "/admin/programa", label: "Programa", icon: Calendar, testid: "admin-nav-programa" },
  { to: "/admin/avisos", label: "Hoy / Destacados", icon: Bell, testid: "admin-nav-avisos" },
  { to: "/admin/sugerencias", label: "Sugerencias", icon: MessageSquare, testid: "admin-nav-sugerencias" },
  { to: "/admin/mensajes", label: "Mensajes", icon: Megaphone, testid: "admin-nav-mensajes" },
  { to: "/admin/pantallas", label: "Pantallas", icon: MonitorPlay, testid: "admin-nav-pantallas" },
  { to: "/admin/control-horario", label: "Control horario", icon: Clock3, testid: "admin-nav-control-horario" },
  { to: "/admin/analytics", label: "Analítica QR", icon: BarChart3, testid: "admin-nav-analytics" },
  { to: "/admin/perfil", label: "Mi perfil", icon: UserCog, testid: "admin-nav-perfil" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [pendingGalleryCount, setPendingGalleryCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/visitor-gallery", { params: { status: "pending" } })
      .then(({ data }) => {
        if (!cancelled) setPendingGalleryCount(Array.isArray(data) ? data.length : 0);
      })
      .catch(() => {
        if (!cancelled) setPendingGalleryCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="admin-layout min-h-screen bg-accent/30">
      <header className="admin-no-print bg-foreground text-background">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
          <Link to="/admin" className="flex items-center gap-2" data-testid="admin-brand">
            <div className="h-8 w-8 rounded-md bg-primary grid place-items-center text-primary-foreground font-black">C</div>
            <div>
              <div className="font-display font-bold leading-tight">Casa del Pueblo</div>
              <div className="text-[11px] uppercase tracking-wider text-background/70">Panel interno</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium" data-testid="admin-user-name">{user?.name}</div>
              <div className="text-[11px] uppercase tracking-wider text-background/70">{user?.role}</div>
            </div>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:brightness-110 transition"
              data-testid="admin-logout-btn"
            >
              <LogOut size={16} />
              Salir
            </button>
          </div>
        </div>
      </header>

      <div className="admin-shell max-w-6xl mx-auto px-4 py-6 grid md:grid-cols-[220px_1fr] gap-6">
        <aside className="admin-no-print md:sticky md:top-4 md:self-start">
          <nav className="bg-white rounded-2xl p-2 border border-border shadow-sm">
            {ITEMS.map((it) => {
              const Icon = it.icon;
              const active = it.end ? pathname === it.to : pathname.startsWith(it.to);
              return (
                <Link
                  key={it.to}
                  to={it.to}
                  data-testid={it.testid}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition ${
                    active ? "bg-primary text-primary-foreground" : "hover:bg-accent"
                  }`}
                >
                  <Icon size={16} />
                  <span className="min-w-0 flex-1">{it.label}</span>
                  {it.to === "/admin/galeria-visitantes" && pendingGalleryCount > 0 && (
                    <span
                      className={`ml-auto grid min-w-5 place-items-center rounded-full px-1.5 py-0.5 text-[11px] font-black ${
                        active ? "bg-white text-destructive" : "bg-destructive text-destructive-foreground"
                      }`}
                      data-testid="admin-nav-galeria-pending-count"
                    >
                      {pendingGalleryCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </aside>

        <main className="admin-main min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
